import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchPipeline, PipelineItem, StageBadge, STAGE_LABELS, daysUntil } from './NewsPage';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { usePageMeta } from '../hooks/usePageMeta';
import LineMap from '../components/legislation/LineMap';
import { ExplainerButton, LegislationExplainer } from '../components/legislation/LegislationExplainer';
import { STATIONS, StationId, LINE_LABELS, countByStation, lineOf, stationIndex, stationOf } from '../components/legislation/lineModel';

/**
 * Forward-looking table of every tracked act in the EU legislative pipeline
 * (Comitology Register + Have Your Say), ordered by next milestone.
 * Dates on drafts are expected, not final, until OJ publication.
 *
 * The metro map above the table counts the acts per station and line and is
 * the table's stage filter. ?explain=1 opens the "how EU food law is made"
 * pop-up on arrival (links from posts and the digest); ?explain=scrutiny (any
 * station id from lineModel.ts) opens it at that station.
 */

function nextMilestone(d: PipelineItem): { label: string; date: string } | null {
  if (d.expected_adoption) return { label: 'Adoption', date: d.expected_adoption };
  if (d.expected_publication) return { label: 'OJ publication', date: d.expected_publication };
  if (d.expected_application) return { label: 'Application', date: d.expected_application };
  return null;
}

function fmt(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function LineChip({ procedure }: { procedure: string | null }) {
  const line = lineOf(procedure);
  if (!line) {
    return (
      <span className="line-chip line-chip--none" title="Procedure not known yet">
        <span aria-hidden="true">–</span><span className="sr-only">Procedure not known yet</span>
      </span>
    );
  }
  const text = `${LINE_LABELS[line].name}: ${LINE_LABELS[line].long.split(':')[0]}`;
  return (
    <span className={`line-chip line-chip--round line-chip--${line.toLowerCase()}`} title={text}>
      <span aria-hidden="true">{line}</span><span className="sr-only">{text}</span>
    </span>
  );
}

export default function LegislationPipelinePage() {
  usePageMeta({ title: 'EU food legislation in the pipeline', description: 'Draft EU food and feed measures from the PAFF committee and Have Your Say, tracked from consultation to the Official Journal, with expected dates.' });
  const [station, setStation] = useState<StationId | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // ?explain=1 opens the pop-up at the first station, ?explain=<station> at that one.
  const explainParam = searchParams.get('explain');
  const explainOpen = explainParam !== null && explainParam !== '0';
  const explainStart = STATIONS.find(s => s.id === explainParam)?.id ?? null;
  const { data, isLoading, isError, error } = useQuery<PipelineItem[], Error>(
    ['legislationPipelineFull'],
    fetchPipeline,
  );

  const relevant = useMemo(
    () => (data || []).filter(d => d.relevance !== 'single_applicant' && d.relevance !== 'out_of_scope'),
    [data],
  );
  const counts = useMemo(() => countByStation(relevant), [relevant]);
  const rows = station ? relevant.filter(d => stationOf(d.stage) === station) : relevant;
  const current = station ? STATIONS[stationIndex(station)] : null;

  function closeExplainer() {
    const next = new URLSearchParams(searchParams);
    next.delete('explain');
    setSearchParams(next, { replace: true });
  }

  return (
    <section className="news-page pipeline-page">
      <div className="pipeline-page__head">
        <div>
          <h1>EU Legislation in the Pipeline</h1>
          <p className="pipeline-page__intro">
            Draft EU food and feed measures tracked from the PAFF committee (Comitology Register) and the
            Commission's Have Your Say portal — before they reach the Official Journal. Dates on drafts are
            <em> expected</em> and only become final on publication.
          </p>
        </div>
        <ExplainerButton linkToPipeline={false} className="legis-explainer-link legis-explainer-link--pill">
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z" />
            <path d="M10 8.5v7l5.5-3.5z" />
          </svg>
          How does EU food law get made?
        </ExplainerButton>
      </div>

      {data && (
        <section className="pipeline-map" aria-labelledby="pipeline-map-title">
          <div className="pipeline-map__head">
            <h2 id="pipeline-map-title" className="pipeline-map__title">On the line today</h2>
            <span className="pipeline-map__note">Acts per station, last 12 months. Pick a station to filter the list.</span>
          </div>
          <LineMap
            variant="live"
            legend
            counts={counts}
            selected={station}
            onSelect={id => setStation(prev => (prev === id ? null : id))}
            label="Filter by station"
          />
        </section>
      )}

      <div className="news-page__subfilter pipeline-page__filter-status">
        <span className="news-page__subfilter-note">
          {rows.length} act(s){current ? ` at ${current.name} · ${current.sub}` : ''}
        </span>
        {current && (
          <button type="button" className="news-page__chip" onClick={() => setStation(null)}>Show all stations</button>
        )}
      </div>

      {isLoading && <LoadingSkeleton rows={8} title={false} />}
      {isError && <div className="error-state">Error: {error?.message}</div>}
      {!isLoading && !isError && rows.length === 0 && (
        <div className="loading-state">No acts in this stage in the last 12 months.</div>
      )}

      {rows.length > 0 && (
        <div className="pipeline-page__table-wrap">
          <table className="pipeline-page__table">
            <thead>
              <tr>
                <th>Line</th>
                <th>Stage</th>
                <th>Act</th>
                <th>Next milestone</th>
                <th>Applies from</th>
                <th>Affects</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(d => {
                const ms = nextMilestone(d);
                const days = daysUntil(d.expected_application);
                return (
                  <tr key={d.dossier_key}>
                    <td><LineChip procedure={d.legislative_procedure} /></td>
                    <td><StageBadge stage={d.stage} /></td>
                    <td>
                      <Link to={`/news?tab=eulaw&dossier=${encodeURIComponent(d.dossier_key)}`}>
                        {d.title || d.title_official}
                      </Link>
                      {d.basic_act_ref && <span className="pipeline-page__amends"> amends {d.basic_act_ref}</span>}
                    </td>
                    <td>{ms ? `${ms.label} ${fmt(ms.date)}` : STAGE_LABELS[d.stage] || '—'}</td>
                    <td>
                      {d.expected_application ? (
                        <>
                          <strong>{fmt(d.expected_application)}</strong>
                          {!d.application_confirmed && <span className="pipeline-page__expected"> expected</span>}
                          {days !== null && days >= 0 && <span className="pipeline-page__expected"> · {days}d</span>}
                        </>
                      ) : 'tbd'}
                    </td>
                    <td className="pipeline-page__affects">
                      {(d.affected_products || []).slice(0, 4).join(', ') || (d.hazard?.name ?? '—')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {explainOpen && <LegislationExplainer start={explainStart} linkToPipeline={false} onClose={closeExplainer} />}
    </section>
  );
}
